import { useMemo, useState } from "react";
import { Search, Package, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";
import { isRawItem } from "@/components/waste-meals/wmUtils";

const C = OPS.wm;

/** «لم أجد الصنف» — the submitting half of item requests (the resolving screen is Phase 3). */
function ItemRequestDialog({ open, onOpenChange, branchId, type, prefill }) {
    const { t, lang } = useI18n();
    const [text, setText] = useState(prefill || "");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post("/waste-meals/item-requests", {
                branch_id: branchId, item_text: text.trim(), type_hint: type, note: note.trim() || null,
            }, { timeout: 15000 });
            toast.success(t("wm_item_request_sent"));
            setText(""); setNote("");
            onOpenChange(false);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={C.itemRequest.dialog} className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t("wm_item_request_title")}</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-[color:var(--ink-muted)]">{t("wm_item_request_hint")}</p>
                <Input data-testid={C.itemRequest.text} value={text} autoFocus
                       onChange={(e) => setText(e.target.value)} placeholder={t("wm_item")} />
                <Textarea data-testid={C.itemRequest.note} value={note} rows={2}
                          onChange={(e) => setNote(e.target.value)} placeholder={t("wm_notes")} />
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("wm_cancel")}</Button>
                    <Button data-testid={C.itemRequest.submit} onClick={submit} disabled={busy || !text.trim()}>
                        {busy ? t("wm_saving") : t("wm_save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Item picker — 128 items on a phone are unusable without grouping, so the
 * list is grouped by `category` with the branch's own items first.
 */
export function ItemPicker({ catalogue, value, onChange, branchId, type }) {
    const { t, lang } = useI18n();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [reqOpen, setReqOpen] = useState(false);

    // Memoised on `catalogue` for the same reason `favSet` below is: the
    // `|| []` fallback mints a NEW array on every render while the catalogue
    // is still loading, which would re-run the `groups` memo every render.
    const items = useMemo(() => catalogue?.items || [], [catalogue]);
    const favSet = useMemo(() => new Set(catalogue?.favourites || []), [catalogue]);
    const selected = items.find((i) => i.id === value);
    const nameOf = (i) => (lang === "ar" ? i.name_ar || i.name_en : i.name_en || i.name_ar);
    const unitOf = (i) =>
        (lang === "ar" ? i.unit_name_ar : i.unit_name_en) || i.unit_name_ar || i.unit_key;

    const groups = useMemo(() => {
        const needle = q.trim().toLowerCase();
        const match = (i) => !needle
            || (i.name_ar || "").toLowerCase().includes(needle)
            || (i.name_en || "").toLowerCase().includes(needle)
            || (i.code || "").toLowerCase().includes(needle);
        const favs = items.filter((i) => favSet.has(i.id) && match(i));
        const rest = items.filter((i) => !favSet.has(i.id) && match(i));
        const byCat = [];
        const seen = new Map();
        for (const i of rest) {
            const k = i.category || "—";
            if (!seen.has(k)) { seen.set(k, []); byCat.push([k, seen.get(k)]); }
            seen.get(k).push(i);
        }
        return { favs, byCat };
    }, [items, favSet, q]);

    const pick = (id) => { onChange(id); setOpen(false); setQ(""); };

    return (
        <>
            <Button type="button" variant="outline" data-testid={C.picker.trigger}
                    onClick={() => setOpen(true)}
                    className="h-10 w-full justify-start gap-2 text-sm font-semibold">
                <Package className="h-4 w-4 text-[color:var(--brand-brown)] shrink-0" />
                <span className="truncate">{selected ? nameOf(selected) : t("wm_pick_item")}</span>
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid={C.picker.dialog} className="max-w-lg p-0 gap-0">
                    <DialogHeader className="px-4 pt-4 pb-2">
                        <DialogTitle className="text-base">{t("wm_pick_item")}</DialogTitle>
                    </DialogHeader>
                    <div className="px-4 pb-2">
                        <div className="relative">
                            <Search className="pointer-events-none absolute top-2.5 h-4 w-4 text-[color:var(--ink-faint)]"
                                    style={{ insetInlineStart: "0.6rem" }} />
                            <Input data-testid={C.picker.search} value={q} autoFocus
                                   onChange={(e) => setQ(e.target.value)}
                                   placeholder={t("wm_search_item")} className="h-9 ps-8" />
                        </div>
                    </div>
                    <div className="max-h-[52vh] overflow-y-auto scrollbar-thin px-2 pb-2">
                        {groups.favs.length ? (
                            <Section label={t("wm_favourites")} items={groups.favs} onPick={pick} nameOf={nameOf} unitOf={unitOf} />
                        ) : null}
                        {groups.byCat.map(([cat, list]) => (
                            <Section key={cat} label={cat} items={list} onPick={pick} nameOf={nameOf} unitOf={unitOf} />
                        ))}
                        {!groups.favs.length && !groups.byCat.length ? (
                            <p data-testid={C.picker.empty} className="px-3 py-6 text-center text-sm text-[color:var(--ink-muted)]">
                                {t("wm_picker_empty")}
                            </p>
                        ) : null}
                    </div>
                    <div className="border-t border-[color:var(--border-soft)] px-4 py-2.5">
                        <button type="button" data-testid={C.picker.requestLink}
                                onClick={() => { setReqOpen(true); setOpen(false); }}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--brand-brown)] hover:underline">
                            <Plus className="h-3.5 w-3.5" />
                            {t("wm_no_item_found")}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            <ItemRequestDialog open={reqOpen} onOpenChange={setReqOpen} branchId={branchId}
                               type={type} prefill={q} />
        </>
    );
}

function Section({ label, items, onPick, nameOf, unitOf }) {
    const { t } = useI18n();
    return (
        <div className="mb-1">
            <p className="sticky top-0 z-10 bg-[color:var(--bg-surface)]/95 px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-[color:var(--ink-faint)] backdrop-blur-sm">
                {label}
            </p>
            {items.map((i) => (
                <button key={i.id} type="button" data-testid={C.picker.option(i.id)}
                        onClick={() => onPick(i.id)}
                        className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] px-3 py-2.5 text-start text-sm text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]">
                    <span className="min-w-0 truncate">{nameOf(i)}</span>
                    <span className="flex shrink-0 items-center gap-1.5 text-[11px] text-[color:var(--ink-faint)]">
                        {/* 044 «جبنة موزاريلا» vs the raw mozzarella: say which is which */}
                        {isRawItem(i) ? (
                            <span data-testid={C.picker.rawBadge(i.id)}
                                  className="rounded-full bg-[color:var(--brand-brown-soft)] px-1.5 py-px text-[10px] font-semibold text-[color:var(--brand-brown-soft-fg)]">
                                {t("wm_raw_badge")}
                            </span>
                        ) : null}
                        {unitOf(i)}
                    </span>
                </button>
            ))}
        </div>
    );
}

export { ItemRequestDialog };
